
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    /* eslint-disable no-underscore-dangle */
import React, { useEffect, useState, useCallback, useLayoutEffect } from 'react';
import { useRouter } from 'next/router';
import debounce from 'lodash/debounce';
import cx from 'classnames';
import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import SharedLibs from 'wizarding-world-web-shared';
import { useUserContext } from '@contexts/user-context';
import { truncate, splitStringByPascalCase } from '@utils/index';
import ScaleText from '@components/ScaleText';
import AnchorLink from '@components/AnchorLink';
import { userHasAccess, resetProfilePagePosition } from '@utils/profile';
import { useWindowWidth } from '@hooks/windowHooks';
import { breakpoints } from '@hooks/useBreakpoints';
import { PROFILE_FIRSTLOAD_PATRONUS, PROFILE_FIRSTLOAD_WAND } from '@constants/index';
import { PROFILE_WAND, WW_WAND, PROFILE_PATRONUS, NEXT_PATRONUS, SORTING_HAT, PROFILE, } from '@constants/routes';
import { trackEvent } from '@utils/analytics';
import { buildContentfulImageURL } from '@utils/image';
import { getGlobalState } from '@states/ProfileState';
import { setCrestLocation, useGlobalState, setHouseAnimationStarted as replayCrestAnimation, } from '@states/ProfileThemeState';
import UserPortrait from './UserPortrait';
import s from './UserPanel.module.scss';
import m from '../../Profile.module.scss';
import FirstWand from '../FirstLoadWand';
import FirstLoadWandText from '../FirstLoadWandText';
import FirstLoadHouseText from '../FirstLoadHouseText';
import BackgroundCircles from '../BackgroundCircles';
import ProfileHouseIcon from '../ProfileHouseIcon';
import unknown from '../../../../images/profile/unknown.svg';
import Image from '../../../../components/Image';
import UserInterests from './UserInterests';
const WandAnimation = dynamic({
    loader: () => import('../WandAnimation'),
    ssr: false
});
const propTypes = {
    profileData: PropTypes.shape({
        membershipBenefits: PropTypes.shape({
            url: PropTypes.string,
            userContent: PropTypes.shape({}),
            fallbackContent: PropTypes.shape({})
        })
    }).isRequired
};
const pageName = 'Profile';
const UserPanel = ({ profileData: { membershipBenefits: membershipBenefitsPromo } }) => {
    const userInterestsEnabled = process.env.REACT_APP_USER_INTERESTS_ENABLED === 'TRUE';
    const router = useRouter();
    const { getCookie, eraseCookie } = SharedLibs.utils.cookie;
    const [node, setNode] = useState(null);
    const wandImage = getGlobalState('userWandImage');
    const patronusImage = getGlobalState('userPatronusImage');
    const [firstLoadHouse] = useGlobalState('firstLoadHouse');
    const [wandAnimationFinished] = useGlobalState('wandAnimationFinished');
    const [mobileOnLoad, setMobileOnLoad] = useState(null);
    const firstLoadPatronus = getCookie(PROFILE_FIRSTLOAD_PATRONUS) === 'true';
    const firstLoadWand = getCookie(PROFILE_FIRSTLOAD_WAND) === 'true';
    const width = useWindowWidth();
    const isMobile = width < breakpoints.xm;
    const [shouldPatronusRepaint, setShouldPatronusRepaint] = useState(false);
    const [shouldWandRepaint, setShouldWandRepaint] = useState(false);
    const { isLoggedIn, profile, profile: { givenName: firstName, familyName: lastName, hogwartsHouse: house = null, wand = null, patronusName: patronus = null, avatar, }, } = useUserContext();
    const avatarCompleted = !!avatar?.avatarId;
    const totalExperiences = 4;
    const completedExperiences = Object.values({
        wand,
        house,
        patronus,
        ...{ avatar: avatar?.avatarId }
    }).filter((val) => !!val).length;
    const houseHref = house ? `${PROFILE}/${house.toLowerCase()}` : SORTING_HAT;
    const wandHref = wand ? PROFILE_WAND : WW_WAND;
    const patronusHref = patronus ? PROFILE_PATRONUS : NEXT_PATRONUS;
    useEffect(() => {
        setMobileOnLoad(isMobile);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        setShouldPatronusRepaint(firstLoadPatronus && !mobileOnLoad);
        setShouldWandRepaint(firstLoadWand && !mobileOnLoad);
    }, [firstLoadPatronus, firstLoadWand, mobileOnLoad]);
    let benefitsPromo = null;
    if (membershipBenefitsPromo) {
        if (membershipBenefitsPromo.contentTypeId === 'userSpecificPromo') {
            /* istanbul ignore next */
            benefitsPromo = userHasAccess(membershipBenefitsPromo, {
                isLoggedIn,
                profile
            })
                ? membershipBenefitsPromo.userContent
                : membershipBenefitsPromo.fallbackContent;
        }
        else {
            benefitsPromo = membershipBenefitsPromo;
        }
    }
    // setting dimensions
    const crestRef = useCallback((crestNode) => {
        setNode(crestNode);
        setCrestLocation(crestNode?.getBoundingClientRect());
    }, []);
    // set dimensions on resize
    useLayoutEffect(() => {
        let debouncedMeasure;
        if (node) {
            const measure = () => {
                setCrestLocation(node?.getBoundingClientRect());
            };
            measure();
            debouncedMeasure = debounce(measure, 250);
            window.addEventListener('resize', debouncedMeasure);
            window.addEventListener('scroll', debouncedMeasure);
        }
        return () => {
            window.removeEventListener('resize', debouncedMeasure);
            window.removeEventListener('scroll', debouncedMeasure);
        };
    }, [node]);
    const removePatronusCookie = () => {
        eraseCookie(PROFILE_FIRSTLOAD_PATRONUS);
    };
    const doTrackEvent = (label) => {
        trackEvent('Icon Clicked', {
            icon: label,
            label,
            location: pageName
        });
    };
    const handleHouseClicked = () => {
        if (router.asPath === houseHref) {
            return;
        }
        if (house) {
            doTrackEvent(`View ${house}`);
        }
        else {
            doTrackEvent('Discover House');
        }
        resetProfilePagePosition();
        router.push(houseHref).then(() => {
            replayCrestAnimation(true);
        });
    };
    const handlePatronusClicked = () => {
        if (router.asPath === PROFILE_PATRONUS)
            return;
        resetProfilePagePosition();
        doTrackEvent('Discover Patronus');
    };
    const handleWandClicked = () => {
        if (router.asPath === PROFILE_WAND)
            return;
        resetProfilePagePosition();
        doTrackEvent('Discover Wand');
    };
    const getCompletionPosition = () => {
        if (!house)
            return 1;
        if (!patronus)
            return 2;
        // else and final return unreachable due to conditions loading getCompletionPosition
        /* istanbul ignore else */
        if (!wand)
            return 3;
        /* istanbul ignore next */
        return 0;
    };
    const patronusImageURL = patronusImage?.displayLogo?.file?.url;
    const patronusSet = [
        {
            src: `${buildContentfulImageURL(patronusImageURL, {
                width: 100,
                fit: 'fill',
                format: 'webp'
            })} 1x, ${buildContentfulImageURL(patronusImageURL, {
                width: 200,
                fit: 'fill',
                format: 'webp'
            })} 2x, ${buildContentfulImageURL(patronusImageURL, {
                width: 300,
                fit: 'fill',
                format: 'webp'
            })} 3x`,
            type: 'image/webp'
        },
        {
            src: `${buildContentfulImageURL(patronusImageURL, {
                width: 100,
                fit: 'fill',
                format: 'jpg'
            })} 1x, ${buildContentfulImageURL(patronusImageURL, {
                width: 200,
                fit: 'fill',
                format: 'jpg'
            })} 2x, ${buildContentfulImageURL(patronusImageURL, {
                width: 300,
                fit: 'fill',
                format: 'jpg'
            })} 3x`,
            type: 'image/jpeg'
        },
        {
            src: buildContentfulImageURL(patronusImageURL, { width: 100, fit: 'fill', format: 'jpg' })
        },
    ];
    const unknownSet = [
        {
            src: unknown
        },
    ];
    return (<div className={[s.userPanel].join(' ')}>
      <div ref={crestRef}>
        <UserPortrait avatarCompleted={avatarCompleted} userHouse={house ? house.toLowerCase() : 'nohouse'} pageName={pageName} avatar={avatar}/>
      </div>
      <div className={s.name}>
        <ScaleText text={[truncate(`${firstName} ${lastName}`, 35)]} fontFamily={`"Harry Beast Regular", serif`} lineMultiplier={1.1} maxFontSizes={{ xs: 32 }}/>
      </div>
      <div className={s.statusContainer}/>

      <div className={s.userActivities}>
        {wand && shouldWandRepaint && (<div className={s.wandAnimationContainer}>
            <WandAnimation />
          </div>)}
        <div className={s.activity}>
          <div className={s.images}>
            <div className={s.resultIcon}>
              <BackgroundCircles house={house ? house.toLowerCase() : 'nohouse'}/>
              <div className={cx(s.icon, s.houseIcon)}>
                <AnchorLink className={s.completionMechanicLink} href={houseHref} title={house ? `View ${house}` : 'Discover House'} onClick={handleHouseClicked}>
                  <ProfileHouseIcon house={house ? house.toLowerCase() : ''}/>
                </AnchorLink>
              </div>
            </div>
          </div>
          <div className={s.activityTitle}>House</div>
          <div className={s.resultName}>
            {house && firstLoadHouse && <FirstLoadHouseText house={house}/>}
            {house && !firstLoadHouse && house}
            {!house && 'Unknown'}
          </div>
        </div>

        <div className={s.activity}>
          <div className={s.images}>
            <div className={cx(s.resultIcon, s.patronusResultIcon)}>
              <BackgroundCircles house={house ? house.toLowerCase() : 'nohouse'}/>
              <div className={s.icon}>
                <AnchorLink className={s.completionMechanicLink} href={patronusHref} title="Discover Patronus" onClick={handlePatronusClicked}>
                  {patronus && patronusImage && shouldPatronusRepaint && (<div onAnimationEnd={removePatronusCookie} className={s.patronusAnimation}/>)}
                  {patronus && patronusImage && (<Image imageSet={patronusSet} contain alt="Patronus image" className={cx(s.patronus, shouldPatronusRepaint ? s.fadeDelay : '')}/>)}
                  {patronus && !patronusImage && (<div className={s.hoverMessage}>
                      <div className={s.missing}>?</div>
                      <div className={s.message}>Oops! Your patronus didn’t load.</div>
                    </div>)}
                  {(!patronus || shouldPatronusRepaint) && (<Image className={cx(s.unknownPatronus, shouldPatronusRepaint ? s.fadeDelayOut : '')} imageSet={unknownSet} alt="Unknown patronus"/>)}
                </AnchorLink>
              </div>
            </div>
          </div>
          <div className={s.activityTitle}>Patronus</div>
          <div className={cx(s.resultName, s.patronusResult)}>
            {!patronus && 'Unknown'}
            {patronus && !shouldPatronusRepaint && patronus}
            {patronus && shouldPatronusRepaint && (<div className={s.firstLoadPatronusText}>
                <div className={s.before}>Unknown</div>
                <div className={s.after}>{patronus}</div>
              </div>)}
          </div>
        </div>

        <div className={s.activity}>
          <div className={s.images}>
            <div className={s.resultIcon}>
              <BackgroundCircles house={house ? house.toLowerCase() : 'nohouse'}/>
              <div className={s.icon}>
                <AnchorLink className={s.completionMechanicLink} href={wandHref} title="Discover Wand" onClick={handleWandClicked}>
                  {wand && wandImage && (<FirstWand isRepainting={shouldWandRepaint && !wandAnimationFinished} showWand={!shouldWandRepaint} wand={`${wandImage?.displayImage?.file?.url}`}/>)}
                  {wand && !wandImage && (<div className={s.hoverMessage}>
                      <div className={s.missing}>?</div>
                      <div className={s.message}>Oops! Your wand didn’t load.</div>
                    </div>)}
                  {!wand && (<Image className={s.unknown} imageSet={unknownSet} alt="Unknown wand"/>)}
                </AnchorLink>
              </div>
            </div>
          </div>
          <div className={s.activityTitle}>Wand</div>
          <div className={s.resultName}>
            {!shouldWandRepaint && wand?.wood && splitStringByPascalCase(wand?.wood)}
            {shouldWandRepaint && wand?.wood && (<FirstLoadWandText wand={splitStringByPascalCase(wand?.wood)}/>)}
            {(!wand || !wand?.wood) && 'Unknown'}
          </div>
        </div>
      </div>

      {completedExperiences < totalExperiences && (<div className={s.profileCompletionStatus}>
          <div className={cx(s.arrow, m.theme, s[`position${getCompletionPosition()}`])}/>
          <div className={cx(s.button, m.theme)}>
            <span>{`${completedExperiences} of ${totalExperiences}: Complete Your Profile`}</span>
          </div>
        </div>)}

      {userInterestsEnabled && <UserInterests />}

      {benefitsPromo && (<AnchorLink className="your-membership-benefits-link" href={`${benefitsPromo.url || `/${benefitsPromo.externalId}`}`} title="Your Membership Benefits" onClick={() => {
                const text = 'Your Membership Benefits';
                trackEvent('CTA Clicked', {
                    cta_text: text,
                    label: text,
                    location: pageName
                });
            }}>
          <div className={cx(s.yourMembershipBenefits, m.theme)}>
            <span>Your Membership Benefits</span>
          </div>
        </AnchorLink>)}
    </div>);
};
UserPanel.propTypes = propTypes;
export default UserPanel;

    async function __Next_Translate__getStaticProps__195ca0e9a77__(ctx) {
      
      return {
        
        
        props: {
          
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: 'src//Profile/components/UserPanel/index',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__195ca0e9a77__ as getStaticProps }
  